/**
 * Mansion public render policy (CRA satellites) — sync via scripts/sync-mansion-public-copy.sh
 */

export const PUBLIC_BRAND = {
  deliveredPriceBadge: 'Guaranteed Delivered Price',
  allInclusiveGateway:
    'All-Inclusive Air Freight Gateway — Fully Cleared to Your Door',
  unifiedNetwork: 'Unified Caribbean Network',
  unifiedNetworkLead:
    'Connect seamlessly with shoppers, curators, and elite island hosts across the unified Digital Hustlers Caribbean network.',
  allInclusiveDeliveredPrice: 'All-Inclusive Delivered Price',
  dutiesIncluded: 'Duties, taxes & clearance included',
  deliveredPriceDisclaimer:
    'All international logistics, regional duties, customs clearance processing, and local taxes are fully included in the displayed total.',
  allInclusiveDeliveryGuaranteeLabel: 'All-Inclusive Delivery Guarantee:',
  allInclusiveDeliveryGuaranteeBody:
    'Clear upfront landed pricing covering all regional logistics, customs duties, and direct-to-door freight clearance processing.',
  conciergeChat: 'Live concierge chat',
  islandChatLead:
    'Connect seamlessly with shoppers, curators, and elite island hosts across the unified Digital Hustlers Caribbean network.',
  naviChatLead:
    'Connect seamlessly with shoppers, curators, and elite island hosts across the unified Digital Hustlers Caribbean network.',
};

const JARGON_REPLACEMENTS = [
  [/\bprofit[- ]?cost\b/gi, 'concierge pricing'],
  [/\bHQ\s+ProfitCost\b/gi, 'All-Inclusive Delivered Price'],
  [/\bProfitCost\s*\([^)]*\)/gi, 'All-Inclusive Delivered Price'],
  [/\bmandatory\s+20%?\s+import\s+markup\s+enforced\b/gi, 'white-glove import clearance included'],
  [/\bmandatory\s+\d+%?\s+import\s+(?:concierge\s+)?markup\b/gi, 'concierge import service'],
  [/\bHQ\s+admin\s+inbox\b/gi, 'concierge team'],
  [/\badmin\s+inbox\b/gi, 'concierge team'],
  [/\bHQ\s+Admin\b/gi, 'Concierge team'],
  [/\bVAT\s*\+\s*margin\b/gi, 'all-inclusive clearance'],
  [/\bincl\.\s*17\.5%\s*VAT\s*\+\s*margin\b/gi, 'all-inclusive clearance'],
  [/\bmargin\s+amount\b/gi, 'service fee'],
  [/\bFinancial\s+Heartbeat\b/gi, 'Live Market Insights'],
  [/\bVAT\s+PROTECTED\b/gi, 'All-Inclusive Pricing'],
  [/\bSupabase\b/gi, 'secure network'],
  [/\bMessages\s+appear\s+in\s+the\s+HQ\s+admin\s+inbox[^.]*\.?/gi, ''],
  [/\bYour\s+messages\s+sync\s+to\s+DHC\s+HQ\s+admin[^.]*\.?/gi, ''],
  [/\blive\s+Supabase\s+feed\b/gi, 'live concierge feed'],
  [/\bSynchronizing\s+canonical\s+data\b/gi, 'Updating live concierge pulse'],
  [/\bSynchronizing\s+Mansion\s+HQ\s+pulse\b/gi, 'Updating live concierge pulse'],
  [/\bincl\.\s*17\.5%\s*VAT[^)]*\)/gi, 'all-inclusive sample pricing)'],
  [/\b17\.5%\s*VAT\b/gi, 'import clearance'],
  [/\blanded\s+cost\b/gi, 'all-inclusive delivered price'],
  [/\blanded\s+price\b/gi, 'delivered price'],
  [/\bprofit-cost\b/gi, 'concierge pricing'],
  [/\bbackend\b/gi, 'concierge'],
  [/\bRLS-isolated tenant records[^.]*\.?/gi, ''],
  [/\bcustoms matrices synced from DHC HQ[^.]*\.?/gi, ''],
  [/\brouted to (the )?management panel[^.]*\.?/gi, ''],
  [/\badmin(istrative)? routing[^.]*\.?/gi, ''],
  [/\bmanagement panel[^.]*\.?/gi, 'concierge team'],
  [/Forbidden use of secret API key in browser/gi, 'Chat is temporarily unavailable. Please refresh.'],
  [/Chat backend not configured/gi, 'Chat is warming up — try again in a moment.'],
];

const OPERATIONAL_LEAKS = [
  'profit-cost',
  'profit cost',
  'profitcost',
  'hq profitcost',
  'margin amount',
  'vat + margin',
  '17.5% vat',
  'canonical data',
  'mandatory 20%',
  'import markup enforced',
  'import markup',
  'admin inbox',
  'supabase',
];

const DEFAULT_FINANCIAL_LABEL =
  '✨ All-Inclusive Delivered Price — Premium Caribbean Concierge';

export function sanitizePublicCopy(input) {
  if (input == null) return '';
  let text = String(input).trim();
  if (!text) return '';
  for (const [pattern, replacement] of JARGON_REPLACEMENTS) {
    text = text.replace(pattern, replacement);
  }
  return text.replace(/\s{2,}/g, ' ').replace(/\s+([,.])/g, '$1').trim();
}

export function isPublicOperationalLeak(text) {
  const sample = String(text).toLowerCase();
  return OPERATIONAL_LEAKS.some((needle) => sample.includes(needle));
}

export function sanitizeFinancialLabel(input, siteFallback) {
  const fallback = siteFallback?.trim()
    ? sanitizePublicCopy(siteFallback)
    : DEFAULT_FINANCIAL_LABEL;
  const sanitized = sanitizePublicCopy(input);
  if (!sanitized || isPublicOperationalLeak(sanitized)) {
    return fallback || DEFAULT_FINANCIAL_LABEL;
  }
  return sanitized;
}

export const DELIVERED_PRICE_BADGE_CLASS_LIGHT =
  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-700 border border-emerald-500/20';

export const DELIVERED_PRICE_DISCLAIMER_CLASS_LIGHT =
  'text-xs font-medium tracking-wide text-slate-600';
